import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { RestaurantDto } from '../../types';
import './Session.css';

type Mode = 'scan' | 'manual';

const CreateSession: React.FC = () => {
  const [mode, setMode] = useState<Mode>('scan');
  const [restaurants, setRestaurants] = useState<RestaurantDto[]>([]);
  const [fetchingRestaurants, setFetchingRestaurants] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Shared fields
  const [restaurantId, setRestaurantId] = useState('');
  const [deliveryFee, setDeliveryFee] = useState('0');
  const [tip, setTip] = useState('0');

  // Scan mode
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get('/restaurants')
      .then((res) => setRestaurants(res.data))
      .catch(() => {})
      .finally(() => setFetchingRestaurants(false));
  }, []);

  const handleScanAndCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receiptFile) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', receiptFile);
      if (restaurantId) formData.append('restaurantId', restaurantId);
      formData.append('deliveryFee', deliveryFee);
      formData.append('tip', tip);

      const res = await api.post('/sessions/scan-and-create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate(`/sessions/${res.data.sessionId}`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create session from receipt');
    } finally {
      setLoading(false);
    }
  };

  const handleManualCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const fee = parseFloat(deliveryFee);
    const tipVal = parseFloat(tip);
    if (isNaN(fee) || fee < 0 || isNaN(tipVal) || tipVal < 0) {
      alert('Delivery fee and tip must be valid non-negative numbers');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/sessions', {
        restaurantId: restaurantId ? parseInt(restaurantId) : null,
        deliveryFee: fee,
        tip: tipVal,
      });
      navigate(`/sessions/${res.data.id}`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1>New Session</h1>

      <div className="mode-tabs">
        <button
          className={mode === 'scan' ? 'tab-btn tab-active' : 'tab-btn'}
          onClick={() => setMode('scan')}
          type="button"
        >
          Scan Receipt
        </button>
        <button
          className={mode === 'manual' ? 'tab-btn tab-active' : 'tab-btn'}
          onClick={() => setMode('manual')}
          type="button"
        >
          Manual
        </button>
      </div>

      {mode === 'scan' ? (
        <form onSubmit={handleScanAndCreate} className="session-form">
          <div className="form-group">
            <label>Receipt Image <span className="required">*</span></label>
            <div className="receipt-input-row">
              <button type="button" className="btn-capture" onClick={() => cameraInputRef.current?.click()}>
                📷 Camera
              </button>
              <button type="button" className="btn-capture" onClick={() => galleryInputRef.current?.click()}>
                🖼 Gallery
              </button>
              {receiptFile && (
                <span className="file-name">{receiptFile.name}</span>
              )}
            </div>
            {/* Hidden: camera */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/jpeg,image/png,image/bmp,image/tiff"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />
            {/* Hidden: gallery */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/jpeg,image/png,image/bmp,image/tiff"
              style={{ display: 'none' }}
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />
            <small>Take a photo or choose from gallery — items will be extracted automatically</small>
          </div>

          <div className="form-group">
            <label>Restaurant <span className="optional">(optional)</span></label>
            {fetchingRestaurants ? (
              <p>Loading...</p>
            ) : (
              <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
                <option value="">No restaurant</option>
                {restaurants.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery Fee</label>
              <input type="number" step="0.01" min="0" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tip</label>
              <input type="number" step="0.01" min="0" value={tip} onChange={(e) => setTip(e.target.value)} />
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading || !receiptFile}>
            {loading ? 'Scanning & Creating...' : 'Scan & Start Session'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleManualCreate} className="session-form">
          <div className="form-group">
            <label>Restaurant <span className="optional">(optional)</span></label>
            {fetchingRestaurants ? (
              <p>Loading...</p>
            ) : (
              <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
                <option value="">No restaurant</option>
                {restaurants.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery Fee</label>
              <input type="number" step="0.01" min="0" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tip</label>
              <input type="number" step="0.01" min="0" value={tip} onChange={(e) => setTip(e.target.value)} />
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Session'}
          </button>
        </form>
      )}
    </div>
  );
};

export default CreateSession;
